import React, { useEffect, useState } from "react";
import { message, Switch, PaginationProps } from "@gw/web-basic-components";
import { PlusCircleOutlined, MinusCircleOutlined } from "@ant-design/icons";
import { cloneDeep } from "lodash";
import { Table, Button } from "@gw/web-basic-components";
import { GWSearch, useBusinessConfig } from "..";
import { TransferName } from "./dto";
import type { ITransferProps } from "./dto";
/**
 * 业务组件 穿梭树
 * @deprecated 1.x版本组件，不再维护，请尽快迁移至本地项目
 */
const GWTransfer = (props: ITransferProps) => {
  const { apis, useRequest } = useBusinessConfig();

  const basePagination = {
    current: 1,
    pageSize: 8,
    total: 0,
    pageSizeOptions: ["8", "15", "20", "50", "100"],
    showSizeChanger: true,
  };
  const [relatedList, setRelatedList] = useState<Record<string, any>[]>(null);
  const [disrelatedList, setDisrelatedList] =
    useState<Record<string, any>[]>(null);
  const [relatedPagination, setRelatedPagination] = useState<PaginationProps>(
    cloneDeep(basePagination)
  );
  const [disrelatedPagination, setDisrelatedPagination] =
    useState<PaginationProps>(cloneDeep(basePagination));

  const [relatedUsersState, fetchRelatedData] = useRequest(
    apis[props.relationName].showRelated
  );
  const [disrelatedState, fetchDisrelatedData] = useRequest(
    apis[props.relationName].showDisrelated
  );
  const [, fetchBind] = useRequest(apis[props.relationName].bind);
  const [, fetchUnbind] = useRequest(apis[props.relationName].unbind);
  const [, fetchRequired] = useRequest(apis[props.relationName].required);
  // 表格搜索
  const [relatedSearchParam, setRelatedSearchParam] = useState(null);
  const [disrelatedSearchParam, setDisrelatedSearchParam] = useState(null);

  /**
   * 获取已关联的数据
   * @force 为ture时，无视未关联数据的拦截
   */
  const getRelatedData = async (force = false) => {
    // todo 目前开启过滤时不调用这个方法
    if (!force && props.searchConfig?.disrelated.isFilter) return;
    const params: Record<string, any> = {
      ...props.record,
      ...relatedSearchParam,
      size: -1,
    };
    if (!props.searchConfig?.related?.notPage) {
      params.current = relatedPagination.current;
      params.size = relatedPagination.pageSize;
    }

    const res: IResDataList = (await fetchRelatedData(params)) as any;

    const config = props.searchConfig?.related;
    let list;
    if (res?.dataList) {
      if (config?.notPage && config?.orderBy) {
        res.dataList.sort(
          (curr: Record<string, any>, next: Record<string, any>) => {
            if (curr[config?.orderBy] > next[config?.orderBy]) return 1;
            return -1;
          }
        );
      }

      list = res.dataList;
    } else {
      list = res;
    }
    if (props?.dataTransform?.related) {
      list = props.dataTransform.related(res);
    }
    setRelatedList(list);
    setRelatedPagination({
      ...relatedPagination,
      total: Number(res.total),
    });
    return list;
  };
  /**
   * 获取未关联的列表数据
   * Todo: 这里需要已关联的数据进行过滤，目前是调用接口实现，不太理想
   */
  const getDisrelatedData = async () => {
    const params: Record<string, any> = {
      ...props.record,
      size: -1,
    };

    if (!props.searchConfig?.related?.notPage) {
      params.current = disrelatedPagination.current;
      params.size = disrelatedPagination.pageSize;
    }
    /**
     * 未关联的接口参数
     */
    const disrelatedParams = { ...params, ...disrelatedSearchParam };
    /**
     * 检查是否初次加载需要加入key、value参数
     * todo 这导致了过渡方案无法去除冗余字段key、value
     */
    if (!disrelatedParams.key && props.extraParam?.key) {
      disrelatedParams.key = props.extraParam.key.join(",");
    }
    if (!disrelatedParams.value && props.extraParam?.value) {
      disrelatedParams.value = props.extraParam.value.join(",");
    }
    const disrelatedData = (await fetchDisrelatedData(
      disrelatedParams
    )) as IResDataList;
    if (props.searchConfig?.disrelated?.isFilter) {
      const { isFilter } = props.searchConfig.disrelated;
      const relatedData: Record<string, any>[] = await getRelatedData(true);

      const flatRelatedData = relatedData
        .map((m) => m[isFilter])
        .filter((m) => m);

      disrelatedData.dataList = disrelatedData.dataList.filter(
        (item) => !flatRelatedData.includes(item[isFilter])
      );
    }
    let list = disrelatedData.dataList;
    if (props?.dataTransform?.disrelated) {
      list = props.dataTransform.disrelated(disrelatedData.dataList);
    }

    setDisrelatedList(list);
    setDisrelatedPagination({
      ...disrelatedPagination,
      total: Number(disrelatedData.total),
    });
  };

  const onClickPlus = (event, record) => {
    fetchBind(props.handleBindApiParam(record))
      .then(() => {
        message.success(props.tipConfig.bind || "关联成功！");
        getRelatedData();
        getDisrelatedData();
        props.onChange?.("plus");
      })
      .catch((error) => {
        console.error("fetchBind error", error);
      });
  };

  const onClickMinus = (event, record) => {
    fetchUnbind(props.handleUnbindApiParam(record))
      .then(() => {
        message.success(props.tipConfig.unbind || "解除关联成功！");
        getRelatedData();
        getDisrelatedData();
        props.onChange?.("minus");
      })
      .catch((error) => {
        console.error("fetchUnbind error", error);
      });
  };

  const onClickCreateBind = async () => {
    const res = await props.operationBarConfig.onClick();
    message.success(
      (props.tipConfig.operation || res || "新建并关联成功！") as any
    );
    getRelatedData();
    getDisrelatedData();
  };

  const onClickRequired = async (record) => {
    await fetchRequired(props.handleRequiredApiParam(record));
    message.success(props.tipConfig.required || "修改成功！");
    getRelatedData(true);
  };

  const IColumns = (colKey) => {
    if (!colKey) return;

    const cols = {
      isRequired: {
        title: "是否必填",
        dataIndex: "attributeRequired",
        key: "attributeRequired",
        render: (text: boolean, record: Record<string, any>) => (
          <div
            style={{ cursor: "pointer" }}
            onClick={() => onClickRequired(record)}
          >
            <Switch checked={Boolean(text)} />
            {/* {text && <CheckCircleFilled style={{ color: "#1BCF9B" }} />}
            {!text && <CloseCircleFilled style={{ color: "#F95353" }} />}
            <SwapOutlined
              style={{ fontSize: "12px", transform: "scale(0.8)" }}
            /> */}
          </div>
        ),
        width: "22%",
      },
    };
    return cols[colKey];
  };

  const relatedColumns = () => {
    const res = [...props.columns];
    if (props.extraColumns?.related?.length) {
      props.extraColumns.related.forEach((item) => {
        if (typeof item === "string") {
          if (IColumns(item)) res.push(IColumns(item));
        } else {
          res.push(item);
        }
      });
    }
    res.push({
      title: "操作",
      dataIndex: "operation",
      key: "operation",
      render: (text, record) => (
        <MinusCircleOutlined
          style={{
            color: text === false ? "#8C8C8C" : "#F95353",
            cursor: "pointer",
          }}
          onClick={(event) =>
            text === false ? null : onClickMinus(event, record)
          }
        />
      ),
      width: "12%",
    });
    return res;
  };
  const disrelatedColumns = () => {
    const res = [...props.columns];
    if (props.extraColumns?.disrelated?.length) {
      props.extraColumns.disrelated.forEach((item) => {
        if (typeof item === "string") {
          if (IColumns(item)) res.push(IColumns(item));
        } else {
          res.push(item);
        }
      });
    }
    res.push({
      title: "操作",
      dataIndex: "operation",
      key: "operation",
      render: (text, record) => (
        <PlusCircleOutlined
          style={{
            color: text === false ? "#8C8C8C" : "#1BCF9B",
            cursor: "pointer",
          }}
          onClick={(event) =>
            text === false ? false : onClickPlus(event, record)
          }
        />
      ),
      width: "12%",
    });
    return res;
  };

  const handleTableChange = (_pagination, _filters, _sorter, tableName) => {
    if (tableName === TransferName.Related) {
      setRelatedPagination({ ..._pagination });
    } else {
      setDisrelatedPagination({ ..._pagination });
    }
  };

  const search = (type: string, inputValue: string, tableName: string) => {
    const params: { key?: string; value?: string; [props: string]: any } = {};
    const queryConditions: {
      column: string;
      matchType: "Like" | "Equals";
      matchValue: string;
    }[] = [];
    const { extraParam } = props;
    if (extraParam?.key && extraParam?.value) {
      params.key = [...extraParam["key"], type].join(",");
      params.value = [...extraParam["value"], inputValue].join(",");
    } else {
      params.key = type;
      params.value = inputValue;
    }
    //todo 此为过渡方案，直到所有的分页查询都改为queryconditons字段
    if (props.searchConfig.disrelated.isFilter) {
      const keys = params.key.split(",");
      const values = params.value.split(",");
      keys.forEach((m, index) => {
        queryConditions.push({
          column: m,
          matchType: "Like",
          matchValue: values[index] ?? "",
        });
      });

      params.queryConditions = queryConditions;
      delete params.key;
      delete params.value;
    }

    if (tableName === TransferName.Related) {
      setRelatedPagination({ ...relatedPagination, current: 1 });
      setRelatedSearchParam({ ...extraParam, ...params });
    } else if (tableName === TransferName.Disrelated) {
      setDisrelatedPagination({ ...disrelatedPagination, current: 1 });
      setDisrelatedSearchParam({ ...extraParam, ...params });
    }
  };
  const initialize = () => {
    setRelatedPagination(cloneDeep(basePagination));
    setDisrelatedPagination(cloneDeep(basePagination));
  };

  const clearSearchParam = () => {
    setDisrelatedSearchParam({ value: "" });
    setRelatedSearchParam({ value: "" });
  };

  useEffect(() => {
    initialize();
    if (!props.visible) {
      clearSearchParam();
    }
  }, [props.visible]);

  useEffect(() => {
    if (props.visible) {
      getRelatedData();
    }
  }, [props.visible]);

  // 监听搜索事件，使用 true 参数强制更新数据
  useEffect(() => {
    if (props.visible) {
      getRelatedData(true);
    }
  }, [
    props.visible,
    props.record.id,
    props.flag,
    relatedPagination.current,
    relatedPagination.pageSize,
    relatedSearchParam,
  ]);

  useEffect(() => {
    if (props.visible) {
      getDisrelatedData();
    }
  }, [
    props.visible,
    props.record.id,
    props.flag,
    disrelatedPagination.current,
    disrelatedPagination.pageSize,
    disrelatedSearchParam,
  ]);

  return (
    <div style={props.style} className="relationForm">
      <div className="content">
        <div className="relatedBlock">
          <div className="head">
            <div className="leftPart">已关联</div>
            {props?.searchConfig?.related?.searchDisplay && (
              <div className="rightPart">
                <GWSearch
                  {...props.searchConfig[TransferName.Related]}
                  onInputChange={(type, inputValue) =>
                    search(type, inputValue, TransferName.Related)
                  }
                  clear={!props.visible}
                  extraParam={props.extraParam}
                />
              </div>
            )}
            {props.operationBarConfig && (
              <Button
                size="small"
                type="primary"
                disabled={props.operationBarConfig.disabled}
                onClick={onClickCreateBind}
              >
                {props.operationBarConfig?.title}
              </Button>
            )}
          </div>
          <div className="body">
            <Table
              columns={relatedColumns()}
              rowKey={(record) => record.id}
              dataSource={relatedList}
              loading={relatedUsersState.loading}
              pagination={
                props.searchConfig?.related?.notPage ? false : relatedPagination
              }
              onChange={(_pagination, _filters, _sorter) =>
                handleTableChange(
                  _pagination,
                  _filters,
                  _sorter,
                  TransferName.Related
                )
              }
            />
          </div>
        </div>
        <div className="disrelatedBlock">
          <div className="head">
            <div className="leftPart">未关联</div>
            {props?.searchConfig && (
              <div className="rightPart">
                <GWSearch
                  {...props.searchConfig[TransferName.Disrelated]}
                  onInputChange={(type, inputValue) =>
                    search(type, inputValue, TransferName.Disrelated)
                  }
                  clear={!props.visible}
                  extraParam={props.extraParam}
                />
              </div>
            )}
          </div>
          <div className="body">
            <Table
              columns={disrelatedColumns()}
              rowKey={(record) => record.id}
              dataSource={disrelatedList}
              loading={disrelatedState.loading}
              pagination={
                props.searchConfig?.disrelated?.notPage
                  ? false
                  : disrelatedPagination
              }
              onChange={(_pagination, _filters, _sorter) =>
                handleTableChange(
                  _pagination,
                  _filters,
                  _sorter,
                  TransferName.Disrelated
                )
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default GWTransfer;
